import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n';
import { toast } from 'sonner';
import { Flag, AlertTriangle, Check } from 'lucide-react';

const ReportButton = ({ postId, userId, type, variant, size }) => {
  const { user, api } = useAuth();
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  // Default props
  const buttonVariant = variant || 'ghost';
  const buttonSize = size || 'sm';
  const reportType = type || 'post';

  const reasons = [
    { id: 'spam', label: 'Spam ou publicité', labelCr: 'Spam ou pub' },
    { id: 'inappropriate', label: 'Contenu inapproprié', labelCr: 'Kontni pa konvnab' },
    { id: 'harassment', label: 'Harcèlement', labelCr: 'Arsèlman' },
    { id: 'misinformation', label: 'Fausse information', labelCr: 'Fos info' },
    { id: 'other', label: 'Autre raison', labelCr: 'Ot rézon' }
  ];

  const handleSubmit = async () => {
    if (!reason) {
      toast.error(language === 'cr' ? 'Shwazi in rézon' : 'Veuillez sélectionner une raison');
      return;
    }

    setLoading(true);
    try {
      const endpoint = reportType === 'post' 
        ? '/posts/' + postId + '/report'
        : '/users/' + userId + '/report';
      
      await api.post(endpoint, {
        reason: reason,
        description: description.trim() || null
      });

      toast.success(language === 'cr' ? 'Sinyalman anvoyé. Mèrsi !' : 'Signalement envoyé. Merci !');
      setOpen(false);
      setReason('');
      setDescription('');
    } catch (error) {
      if (error.response && error.response.status === 400) {
        toast.error(language === 'cr' ? 'Ou la déja sinyalé sa' : 'Vous avez déjà signalé ce contenu');
      } else {
        toast.error(language === 'cr' ? 'Nafèr' : 'Erreur lors du signalement');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReasonSelect = (reasonId) => {
    setReason(reasonId);
  };

  if (!user) return null;

  const targetId = postId || userId;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant={buttonVariant} 
          size={buttonSize}
          className="text-muted-foreground hover:text-destructive"
          data-testid={'report-btn-' + reportType + '-' + targetId}
        >
          <Flag className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            {language === 'cr' 
              ? 'Sinyalé sa ' + (reportType === 'post' ? 'post' : 'profil') + ' la'
              : 'Signaler ce ' + (reportType === 'post' ? 'post' : 'profil')}
          </DialogTitle>
          <DialogDescription>
            {language === 'cr'
              ? 'Éd anou gard in kominité rèspèktyèz.'
              : 'Aidez-nous à maintenir une communauté saine.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-3">
            <Label>{language === 'cr' ? 'Rézon lo sinyalman' : 'Raison du signalement'}</Label>
            <div className="space-y-2">
              {reasons.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => handleReasonSelect(r.id)}
                  className={'w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ' + 
                    (reason === r.id 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:bg-muted/50')}
                >
                  <span className="flex-1">{language === 'cr' ? r.labelCr : r.label}</span>
                  {reason === r.id && <Check className="h-4 w-4 text-primary" />}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              {language === 'cr' ? 'Détay (opsyonèl)' : 'Détails (optionnel)'}
            </Label>
            <Textarea
              id="description"
              placeholder={language === 'cr' ? 'Dékri lo problèm...' : 'Décrivez le problème...'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {language === 'cr' ? 'Anilé' : 'Annuler'}
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!reason || loading}
            variant="destructive"
            data-testid="submit-report-btn"
          >
            {loading 
              ? (language === 'cr' ? 'Anvoy...' : 'Envoi...') 
              : (language === 'cr' ? 'Anvoy' : 'Envoyer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportButton;
